import { useEffect, useRef, type ReactNode } from 'react'
import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'

const OSM_STYLE: maplibregl.StyleSpecification = {
  version: 8,
  sources: {
    osm: {
      type: 'raster',
      tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
      tileSize: 256,
      attribution: '© OpenStreetMap contributors',
    },
  },
  layers: [{ id: 'osm', type: 'raster', source: 'osm' }],
}

const DEFAULT_CENTER: [number, number] = [-9.1393, 38.7223] // Lisbon
const MAX_PINS = 200

export interface MapPin {
  id: number
  name: string
  lat: number
  lng: number
  category?: string | null
  status?: string | null
}

interface MapListViewProps {
  pins: MapPin[]
  center: { lat: number; lng: number } | null
  children: ReactNode
  onPinClick?: (id: number) => void
  className?: string
}

export function MapListView({ pins, center, children, onPinClick, className }: MapListViewProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef       = useRef<maplibregl.Map | null>(null)
  const markersRef   = useRef<maplibregl.Marker[]>([])
  const visiblePins  = pins.slice(0, MAX_PINS)
  const truncated    = pins.length > MAX_PINS

  useEffect(() => {
    if (!containerRef.current) return

    const initialCenter: [number, number] = center
      ? [center.lng, center.lat]
      : visiblePins.length > 0
      ? [visiblePins[0].lng, visiblePins[0].lat]
      : DEFAULT_CENTER

    const map = new maplibregl.Map({
      container: containerRef.current,
      style:     OSM_STYLE,
      center:    initialCenter,
      zoom:      12,
    })
    map.addControl(new maplibregl.NavigationControl(), 'top-right')
    mapRef.current = map

    map.on('load', () => {
      // Place markers
      markersRef.current = visiblePins.map((pin) => {
        const el = document.createElement('div')
        el.className = 'w-3 h-3 rounded-full border-2 border-white bg-blue-600 cursor-pointer shadow'
        el.title = pin.name

        const marker = new maplibregl.Marker({ element: el })
          .setLngLat([pin.lng, pin.lat])
          .addTo(map)

        if (onPinClick) {
          el.addEventListener('click', (e) => {
            e.stopPropagation()
            onPinClick(pin.id)
          })
        }

        return marker
      })

      // Auto-fit to all pins
      if (visiblePins.length > 1) {
        const bounds = new maplibregl.LngLatBounds()
        visiblePins.forEach((p) => bounds.extend([p.lng, p.lat]))
        map.fitBounds(bounds, { padding: 40, maxZoom: 14 })
      }
    })

    return () => {
      markersRef.current.forEach((m) => m.remove())
      markersRef.current = []
      map.remove()
      mapRef.current = null
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={`flex flex-col gap-0 lg:grid lg:grid-cols-2 ${className ?? ''}`}>
      <div className="relative">
        <div ref={containerRef} className="h-64 lg:h-full min-h-[400px]" />
        {truncated && (
          <div className="absolute bottom-2 left-2 right-2 rounded bg-yellow-50 border border-yellow-200 px-3 py-1.5 text-xs text-yellow-800">
            Showing first {MAX_PINS} of {pins.length} locations
          </div>
        )}
      </div>
      <div className="overflow-y-auto border-t lg:border-t-0 lg:border-l">
        {children}
      </div>
    </div>
  )
}
